import { FC, useMemo } from 'react';
import { Box, Switch, Typography } from '@mui/material';
import { LoadingSpinner } from '@components';
import { useGetAllServicesQuery, useUpdateServiceMutation } from '@services';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { ServiceStatus } from '@models';
import { showToast } from '@store/slices/notification';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import { ALL_SERVICES } from '@constants';

type ServiceRow = {
  id: string;
  name: string;
  message: string;
  status: string;
  toggle: ServiceStatus;
};

const ServicesPage: FC = () => {
  const dispatch = useAppDispatch();
  const { data: serviceStatuses, isLoading: isLoadingServices, isError } = useGetAllServicesQuery();
  const [updateService] = useUpdateServiceMutation();

  const activeUser = useAppSelector((state) => state.activeUser.value);
  const userAccessStatus = activeUser?.user?.roles.find((role: any) => role.moduleName === 'services');
  const hasWriteAccess = userAccessStatus.role > 1;

  const toggleService = (serviceStatus: ServiceStatus | undefined) => {
    serviceStatus &&
      updateService({ ...serviceStatus, is_active: !serviceStatus.is_active })
        .unwrap()
        .then(() => {
          dispatch(
            showToast({
              message: `Updated ${serviceStatus.service} successfully!`,
              type: 'success',
            }),
          );
        })
        .catch(() => {
          dispatch(
            showToast({
              message: `Failed to update ${serviceStatus.service} service`,
              type: 'error',
            }),
          );
        });
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'message', headerName: 'Message', flex: 2 },
    { field: 'status', headerName: 'Status', flex: 1 },
    {
      field: 'toggle',
      headerName: 'Enable Service',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<ServiceStatus>) =>
        params.value && (
          <Switch checked={!!params.value.is_active} onChange={() => toggleService(params.value)} disabled={!hasWriteAccess} />
        ),
      flex: 1,
      align: 'center',
    },
  ];

  const rows: ServiceRow[] = useMemo(() => {
    // Filter the disabled services retrieved from AVA from the hardcoded services so we don't show duplicates.
    const filteredEnabledServices = ALL_SERVICES.filter(
      (service) => !serviceStatuses?.find((offlineService) => offlineService.service === service),
    );

    const enabledServices: ServiceRow[] = filteredEnabledServices?.map((service) => ({
      id: service,
      name: service,
      message: '',
      status: 'Online',
      toggle: { message: `Service disabled through Jimbo by ${activeUser.user.email}`, service, is_active: true } as ServiceStatus,
    }));
    const disabledServices: ServiceRow[] = serviceStatuses
      ? serviceStatuses.map((serviceStatus) => ({
          id: serviceStatus.service,
          name: serviceStatus.service,
          message: serviceStatus.message,
          status: serviceStatus.is_active ? 'Online' : 'Offline',
          toggle: serviceStatus,
        }))
      : [];

    return [...enabledServices, ...disabledServices];
  }, [activeUser.user.email, serviceStatuses]);

  const renderContent = () => {
    if (isLoadingServices) {
      return <LoadingSpinner isOpen={true} />;
    }

    if (isError) {
      return (
        <Typography variant="subtitle1" marginBottom={2} textAlign="center">
          Unable to retrieve services.
        </Typography>
      );
    }

    return <DataGrid rows={rows} columns={columns} hideFooter autoHeight />;
  };

  return (
    <Box my={4} height="100%">
      {renderContent()}
    </Box>
  );
};

export default ServicesPage;
